'use strict';

/**
 * SSO Utility Functions
 * Centralized functions for SSO operations following EatLove DRY principles
 */

/**
 * Generate cryptographically secure random string for CSRF protection
 * Used for state parameter generation in OIDC flows
 * @returns {string} Random string with high entropy
 */
export function generateSecureRandom() {
    // Use crypto.randomUUID() for maximum entropy when available
    if (window.crypto && window.crypto.randomUUID) {
        return window.crypto.randomUUID().replace(/-/g, '').substring(0, 12);
    }

    // Fallback to crypto.getRandomValues for good entropy
    if (window.crypto && window.crypto.getRandomValues) {
        const array = new Uint32Array(2);
        window.crypto.getRandomValues(array);
        return array[0].toString(36) + array[1].toString(36);
    }

    // Final fallback for very old browsers
    return Math.random().toString(36).substring(2, 11);
}

/**
 * Generate organization initials from organization name
 * Used for fallback avatars when organization logo is not available
 * @param {string} organizationName - Full organization name
 * @returns {string} Organization initials (max 2 characters, uppercase)
 */
export function getOrgInitials(organizationName) {
    if (!organizationName) return 'ORG';

    const words = organizationName.trim().split(/\s+/);
    if (words.length === 1) {
        return words[0].substring(0, 2).toUpperCase();
    }

    return words
        .slice(0, 2)
        .map((word) => word[0])
        .join('')
        .toUpperCase();
}

/**
 * Check if the user is eligible for SSO based on their profile
 * Eligibility is determined by the presence of an authorization URL in user links
 * @param {Object} user - User object containing profile information
 * @returns {boolean} True if user is eligible for SSO, false otherwise
 */
export const isSSOEligible = (user) => {
    if (!user || !user.links) {
        return false;
    }

    return user?.links['authorization-url'];
};
